import { Controller } from "@hotwired/stimulus"

// Locks submit buttons when a form is submitted: disables them to prevent a
// double-submit and swaps the label to a "working…" message, so slow POSTs
// (e.g. checkout → Flouci, ~1.5s) don't feel frozen. Used on the buyer-facing
// payment/booking forms, which are data-turbo="false" — Turbo's native
// data-turbo-submits-with does not fire for them.
//
// Attach data-controller="submit-lock" to the <form> and
// data-submit-lock-target="button" + data-submit-lock-label="…" to each
// submit button.
export default class extends Controller {
  static targets = ["button"]

  connect() {
    this.element.addEventListener("submit", this.lock)
    // Browser back/forward (bfcache) can restore the page mid-submit with the
    // button still disabled — reset it so the form stays usable.
    window.addEventListener("pageshow", this.reset)
  }

  disconnect() {
    this.element.removeEventListener("submit", this.lock)
    window.removeEventListener("pageshow", this.reset)
  }

  lock = () => {
    // Defer so the form still submits with the button's name/value, then
    // disable it to block a second click.
    setTimeout(() => {
      this.buttonTargets.forEach((btn) => {
        const label = btn.dataset.submitLockLabel
        if (label) {
          // innerHTML, pas textContent : le bouton du tunnel porte des <span>
          // (libellé + prix, cibles de funnel-checkout). Restaurer du texte nu
          // perdrait ces cibles au retour bfcache.
          btn.dataset.submitLockOriginal ??=
            btn.tagName === "INPUT" ? btn.value : btn.innerHTML
          if (btn.tagName === "INPUT") {
            btn.value = label
          } else {
            btn.textContent = label
          }
        }
        btn.dataset.submitLockLocked = "1"
        btn.disabled = true
        btn.setAttribute("aria-busy", "true")
      })
    }, 0)
  }

  // ⛔ 08/09/2026. `pageshow` ne se déclenche PAS qu'au retour bfcache : il part
  // à CHAQUE chargement de page. reset() rouvrait donc, sur toute page, tout
  // bouton que le serveur avait rendu `disabled` — y compris le « Paiement
  // indisponible » d'un vendeur sans moyen de paiement. Vu au navigateur le
  // 08/09/2026 : assert_select voyait bien `disabled` dans le HTML, Chromium
  // affichait un bouton cliquable. Le test lisait le rendu, pas le DOM.
  //
  // Deux verrous : on n'agit que sur une vraie restauration bfcache
  // (event.persisted), et on ne réactive QUE les boutons que lock() a
  // lui-même désactivés — jamais un `disabled` venu du serveur.
  reset = (event) => {
    if (event && event.persisted !== true) return
    this.buttonTargets.forEach((btn) => {
      if (btn.dataset.submitLockLocked !== "1") return
      const original = btn.dataset.submitLockOriginal
      if (original != null) {
        if (btn.tagName === "INPUT") {
          btn.value = original
        } else {
          btn.innerHTML = original
        }
      }
      delete btn.dataset.submitLockLocked
      btn.disabled = false
      btn.removeAttribute("aria-busy")
    })
  }
}
